import Border, { IBorderValueProps } from '../../../puck/reusable-props/Border';
import { MarginType } from '../../../puck/reusable-props/margin';
import { PaddingType } from '../../../puck/reusable-props/padding';
import { Switch, ToggleButton, ToggleButtonGroup } from '@mui/material';
import ColorPicker from '../../../puck/reusable-props/ColorPicker';
import PaddingComponent from '../../customComponents/PaddingComponent';
import MarginComponent from '../../customComponents/MarginComponent';
import WithAccordion from '../../customComponents/CustomAccordion';
import BackgroungColorComponent, {
  IBackgroundProps,
} from '../../customComponents/BackgroungColorComponent';
import { FieldLabel } from '@measured/puck';
import CustomTooltip from '../../../../components/common/tooltip';
import QuilHelper from '../../../puck/reusable-props/quilHelper';
import { Select } from '../../form-fields/Select';
import {
  fontSizeOptions,
  fontWeightOptions,
} from '../../../puck/reusable-props/font';
import WidthComponent, {
  IWidthComponentProps,
} from '../../blocks/widthComponent';
import { heightOptions } from '../../../puck/reusable-props/height';
import { widthOptions } from '../../../puck/reusable-props/width';
import { deviceOptions, JustifyOptions } from '../../../utils/constants';
import { IJustifyContent } from './Body';
import Toggle from '../../../../components/common/toggleButton';

export interface HeaderProps {
  background: IBackgroundProps;
  border: IBorderValueProps;
  margin: MarginType;
  padding: PaddingType;
  width: IWidthComponentProps;
  maxWidth: IWidthComponentProps;
  justifyContent: IJustifyContent;
  collapse: {
    currentDevice: 'mobile' | 'tablet' | 'desktop';
    showAccordion: {
      mobile: {
        show: boolean;
        defaultCollapsed: boolean;
      };
      tablet: {
        show: boolean;
        defaultCollapsed: boolean;
      };
      desktop: {
        show: boolean;
        defaultCollapsed: boolean;
      };
    };
  };
  orderAmount: {
    show: boolean;
    color: string;
    fontSize: string;
    fontWeight: string;
  };
  iconPlacement: 'left' | 'right';
  text: string;
  icon: {
    show: boolean;
    color: string;
    textColor: string;
    textShow: boolean;
    width: string;
    height: string;
  };
}

interface IProps {
  value: HeaderProps;
  onChange: (_value: HeaderProps) => void;
}

const AccordionHeader = ({ value, onChange }: IProps) => {
  return (
    <WithAccordion title="Header">
      <WithAccordion title="Collapse">
        <div className="grid gap-2">
          <FieldLabel
            label={
              (
                <CustomTooltip title="device">
                  <span>Device</span>
                </CustomTooltip>
              ) as React.ReactNode as string
            }
            el="div">
            <ToggleButtonGroup
              value={value?.collapse?.currentDevice}
              exclusive
              className="w-full"
              onChange={(_e, currentDevice) =>
                currentDevice &&
                onChange({
                  ...value,
                  collapse: {
                    ...value.collapse,
                    currentDevice,
                  },
                })
              }>
              <ToggleButton
                size="small"
                disableRipple
                value="mobile"
                className="flex-1">
                Mobile
              </ToggleButton>
              <ToggleButton
                size="small"
                value="tablet"
                disableRipple
                className="flex-1">
                Tablet
              </ToggleButton>
              <ToggleButton
                size="small"
                value="desktop"
                disableRipple
                className="flex-1">
                Desktop
              </ToggleButton>
            </ToggleButtonGroup>
          </FieldLabel>
          <div className="flex gap-2 items-center justify-between">
            <label>Show Accordion</label>
            <Switch
              checked={
                value?.collapse?.showAccordion?.[value?.collapse?.currentDevice]
                  ?.show
              }
              onChange={event => {
                onChange({
                  ...value,
                  collapse: {
                    ...value.collapse,
                    showAccordion: {
                      ...value.collapse.showAccordion,
                      [value.collapse.currentDevice]: {
                        ...value.collapse.showAccordion[
                          value.collapse.currentDevice
                        ],
                        show: event.target.checked,
                      },
                    },
                  },
                });
              }}
              inputProps={{ 'aria-label': 'controlled' }}
            />
          </div>
          {value?.collapse?.showAccordion?.[value?.collapse?.currentDevice]
            ?.show && (
            <div className="flex gap-2 items-center justify-between">
              <label>Default Collapsed</label>
              <Switch
                checked={
                  value?.collapse?.showAccordion?.[
                    value?.collapse?.currentDevice
                  ]?.defaultCollapsed
                }
                onChange={event => {
                  onChange({
                    ...value,
                    collapse: {
                      ...value.collapse,
                      showAccordion: {
                        ...value.collapse.showAccordion,
                        [value.collapse.currentDevice]: {
                          ...value.collapse.showAccordion[
                            value.collapse.currentDevice
                          ],
                          defaultCollapsed: event.target.checked,
                        },
                      },
                    },
                  });
                }}
                inputProps={{ 'aria-label': 'controlled' }}
              />
            </div>
          )}
        </div>
      </WithAccordion>
      <PaddingComponent
        value={value.padding}
        onChange={padding => onChange({ ...value, padding })}
        title="Padding"
      />
      <MarginComponent
        value={value.margin}
        onChange={margin => onChange({ ...value, margin })}
        title="Margin"
      />
      <WidthComponent
        onChange={width => onChange({ ...value, width })}
        value={value.width}
      />
      <WidthComponent
        onChange={maxWidth => onChange({ ...value, maxWidth })}
        value={value.maxWidth}
        title="Max Width"
      />
      <Border
        value={value.border}
        onChange={border => {
          onChange({ ...value, border });
        }}
        title="Border"
      />
      <BackgroungColorComponent
        value={value.background}
        onChange={background => onChange({ ...value, background })}
      />
      <WithAccordion title="Icon Controls">
        <>
          <div className="flex justify-between items-center mb-3">
            <p className="text-sm font-semibold">Show Icon</p>
            <Switch
              checked={value?.icon?.show}
              onChange={e =>
                onChange({
                  ...value,
                  icon: {
                    ...value.icon,
                    show: e.target.checked,
                  },
                })
              }
            />
          </div>
          {value?.icon?.show && (
            <Toggle<HeaderProps>
              value={value}
              onChange={newValue => {
                onChange({ ...value, ...newValue });
              }}
              heading={{ label: 'Icon Placement', value: 'iconPlacement' }}
              defaultValue="left"
              options={[
                { label: 'Left', value: 'left' },
                { label: 'Right', value: 'right' },
              ]}
              propName="iconPlacement"
            />
          )}
          {value.iconPlacement === 'right' && (
            <div className="flex justify-between items-center mb-3">
              <p className="text-sm font-semibold">Show toggle text</p>
              <Switch
                checked={value?.icon?.textShow}
                onChange={e =>
                  onChange({
                    ...value,
                    icon: {
                      ...value.icon,
                      textShow: e.target.checked,
                    },
                  })
                }
              />
            </div>
          )}
          {value?.icon?.textShow && value.iconPlacement === 'right' && (
            <ColorPicker
              label="Toggle text color"
              value={value?.icon?.textColor}
              onChange={textColor =>
                onChange({ ...value, icon: { ...value.icon, textColor } })
              }
              tooltip="Icon Color"
            />
          )}
          <ColorPicker
            label="Icon Color"
            value={value.icon.color}
            onChange={color =>
              onChange({ ...value, icon: { ...value.icon, color } })
            }
            tooltip="Icon Color"
          />
          <FieldLabel
            label={
              (
                <CustomTooltip title="fontWeight">
                  <span>Height</span>
                </CustomTooltip>
              ) as React.ReactNode as string
            }
            el="div">
            <Select
              value={value?.icon?.height}
              onChange={event =>
                onChange({
                  ...value,
                  icon: {
                    ...value.icon,
                    height: event.target.value,
                  },
                })
              }
              options={heightOptions}
            />
          </FieldLabel>
          <FieldLabel
            label={
              (
                <CustomTooltip title="fontWeight">
                  <span>Weight</span>
                </CustomTooltip>
              ) as React.ReactNode as string
            }
            el="div">
            <Select
              value={value?.icon?.width}
              onChange={event =>
                onChange({
                  ...value,
                  icon: {
                    ...value.icon,
                    width: event.target.value,
                  },
                })
              }
              options={widthOptions}
            />
          </FieldLabel>
        </>
      </WithAccordion>
      <WithAccordion title="Order Amount">
        <>
          <div className="flex justify-between items-center mb-3">
            <p className="text-sm font-semibold">Show Amount</p>
            <Switch
              checked={value?.orderAmount?.show}
              onChange={e =>
                onChange({
                  ...value,
                  orderAmount: {
                    ...value.orderAmount,
                    show: e.target.checked,
                  },
                })
              }
            />
          </div>
          <ColorPicker
            label="Amount Color"
            value={value.orderAmount.color}
            onChange={color =>
              onChange({
                ...value,
                orderAmount: { ...value.orderAmount, color },
              })
            }
            tooltip="Amount Color"
          />
          <FieldLabel
            label={
              (
                <CustomTooltip title="fontSize">
                  <span>Size</span>
                </CustomTooltip>
              ) as React.ReactNode as string
            }
            el="div">
            <Select
              className="pagebuilder-theme"
              value={value?.orderAmount?.fontSize}
              onChange={event =>
                onChange({
                  ...value,
                  orderAmount: {
                    ...value.orderAmount,
                    fontSize: event.target.value,
                  },
                })
              }
              options={fontSizeOptions}
            />
          </FieldLabel>
          <FieldLabel
            label={
              (
                <CustomTooltip title="fontWeight">
                  <span>Weight</span>
                </CustomTooltip>
              ) as React.ReactNode as string
            }
            el="div">
            <Select
              value={value?.orderAmount?.fontWeight}
              onChange={event =>
                onChange({
                  ...value,
                  orderAmount: {
                    ...value.orderAmount,
                    fontWeight: event.target.value,
                  },
                })
              }
              options={fontWeightOptions}
            />
          </FieldLabel>
        </>
      </WithAccordion>
      <WithAccordion title="Justify Content">
        <Toggle<{ label: string; value: string; deviceType: string }>
          value={
            deviceOptions?.find(
              item => item.value === value?.justifyContent?.currentDevice,
            ) || deviceOptions?.[0]
          }
          onChange={newValue => {
            onChange({
              ...value,
              justifyContent: {
                ...value?.justifyContent,
                currentDevice:
                  newValue?.deviceType as typeof value.justifyContent.currentDevice,
              },
            });
          }}
          heading={{ label: 'Device', value: 'device' }}
          options={deviceOptions}
          propName="deviceType"
        />
        <Select
          className="pagebuilder-theme"
          value={
            value?.justifyContent?.justify?.[
              value?.justifyContent?.currentDevice
            ] || ''
          }
          onChange={e =>
            onChange({
              ...value,
              justifyContent: {
                ...value?.justifyContent,
                justify: {
                  ...value.justifyContent.justify,
                  [value?.justifyContent?.currentDevice]: e.target.value,
                },
              },
            })
          }
          options={JustifyOptions}
          label="Justify Content"
        />
      </WithAccordion>
      <FieldLabel
        label={
          (
            <CustomTooltip title="QuillEditor">
              <span>{`Text`}</span>
            </CustomTooltip>
          ) as React.ReactNode as string
        }
        el="div">
        <QuilHelper
          value={value?.text}
          onChange={(k: string) => onChange({ ...value, text: k })}
          enableLink={true}
        />
      </FieldLabel>
    </WithAccordion>
  );
};

export default AccordionHeader;
